import { AdSdkError } from "./requester";
import { fireTrackingUrl } from "./tracker";
import type {
  AdDecision,
  AdLifecycleEvent,
  AdRenderOptions,
  AdRenderTarget,
  BannerAsset,
  ImageAsset,
  NativeAsset,
  VideoAdSession,
  VideoAsset,
  VideoLabels,
  VideoState,
} from "./types";

const MAX_Z_INDEX = "2147483647";
const SIZE_TOLERANCE_PX = 0.5;
const VIDEO_FALLBACK_WIDTH = 640;
const VIDEO_FALLBACK_HEIGHT = 360;
const SDK_STYLE_ID = "adsense-sdk-renderer-styles";
const PROTECTED_IMAGE_CHANNEL = "adsense-protected-image";
const PROTECTED_IMAGE_TIMEOUT_MS = 3000;
const PROTECTED_IFRAME_SANDBOX =
  "allow-scripts allow-popups allow-popups-to-escape-sandbox";
const PROTECTED_IFRAME_REFERRER_POLICY = "strict-origin-when-cross-origin";

export class AdRenderError extends AdSdkError {
  constructor(message: string) {
    super(message, "RENDER_ERROR");
    this.name = "AdRenderError";
  }
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function isBannerAsset(asset: AdDecision["asset"]): asset is BannerAsset {
  return (
    "url" in asset &&
    isPositiveFiniteNumber(asset.width) &&
    isPositiveFiniteNumber(asset.height)
  );
}

function isVideoAsset(asset: AdDecision["asset"]): asset is VideoAsset {
  return "url" in asset && typeof asset.url === "string";
}

function isNativeAsset(asset: AdDecision["asset"]): asset is NativeAsset {
  return "title" in asset && "description" in asset;
}

function ensureSdkStyles(): void {
  if (
    typeof document === "undefined" ||
    typeof document.getElementById !== "function" ||
    document.getElementById(SDK_STYLE_ID) ||
    !document.head
  ) {
    return;
  }

  const style = document.createElement("style");
  style.id = SDK_STYLE_ID;
  style.textContent = `
[data-adsense-slot="true"] {
  position: relative !important;
  z-index: 2147483647 !important;
  transform: none !important;
  clip-path: none !important;
  border-radius: 0 !important;
  opacity: 1 !important;
  filter: none !important;
  mask: none !important;
  -webkit-mask: none !important;
  perspective: none !important;
  zoom: 1 !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  flex: none !important;
  flex-shrink: 0 !important;
}
[data-adsense-host="true"] {
  position: relative !important;
  z-index: 2147483647 !important;
  transform: none !important;
  clip-path: none !important;
  border-radius: 0 !important;
  opacity: 1 !important;
  filter: none !important;
  mask: none !important;
  -webkit-mask: none !important;
  perspective: none !important;
  zoom: 1 !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  flex: none !important;
  flex-shrink: 0 !important;
}
.adsense-video-shell {
  background: #05070b;
  color: #fff;
  font-family: Arial, sans-serif;
}
.adsense-video-shell,
.adsense-video-shell * {
  box-sizing: border-box;
}
.adsense-video-media {
  background: #07111f;
  object-fit: contain;
}
.adsense-video-click {
  cursor: pointer;
}
.adsense-video-controls {
  align-items: center;
  background: transparent;
  bottom: 0;
  display: flex;
  gap: clamp(5px, 1.1vw, 8px);
  left: 0;
  min-height: clamp(34px, 7vw, 44px);
  padding: clamp(7px, 1.5vw, 11px) clamp(7px, 1.6vw, 11px) clamp(7px, 1.4vw, 9px);
  position: absolute;
  right: 0;
  z-index: 3;
}
.adsense-video-controls[data-hidden="true"] {
  display: none;
}
.adsense-video-button {
  align-items: center;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: clamp(11px, 1.9vw, 13px);
  font-weight: 700;
  height: clamp(26px, 5.2vw, 32px);
  justify-content: center;
  min-width: clamp(32px, 6.5vw, 40px);
  padding: 0 clamp(8px, 2vw, 12px);
}
.adsense-video-icon-button {
  min-width: clamp(26px, 5.2vw, 32px);
  padding: 0;
  width: clamp(26px, 5.2vw, 32px);
}
.adsense-video-icon {
  display: block;
  flex: 0 0 auto;
  height: clamp(13px, 2.8vw, 16px);
  pointer-events: none;
  width: clamp(13px, 2.8vw, 16px);
}
.adsense-video-progress {
  background: rgba(255, 255, 255, 0.18);
  bottom: 0;
  box-shadow: none;
  cursor: default;
  height: 4px;
  left: 0;
  margin: 0;
  min-width: 0;
  position: absolute;
  right: 0;
  width: 100%;
}
.adsense-video-progress[data-seekable="true"] {
  cursor: pointer;
}
.adsense-video-progress:focus-within {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
}
.adsense-video-progress-fill {
  background: rgba(255, 255, 255, 0.72);
  height: 100%;
  pointer-events: none;
  width: 0;
}
.adsense-video-progress-input {
  -webkit-appearance: none;
  appearance: none;
  accent-color: transparent;
  background: transparent;
  border: 0;
  bottom: -10px;
  box-shadow: none;
  cursor: pointer;
  height: 24px;
  left: 0;
  margin: 0;
  opacity: 0;
  padding: 0;
  position: absolute;
  width: 100%;
}
.adsense-video-countdown {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 3;
  color: rgba(243, 246, 250, 0.94);
  font-size: clamp(12px, 2vw, 14px);
  font-weight: 700;
  line-height: 1;
  padding: clamp(6px, 1.2vw, 8px) clamp(8px, 1.8vw, 11px);
  pointer-events: none;
}
.adsense-video-skip {
  -webkit-backdrop-filter: blur(14px) saturate(145%);
  backdrop-filter: blur(14px) saturate(145%);
  background: rgba(31, 41, 55, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-right: 0;
  border-radius: clamp(10px, 2vw, 16px) 0 0 clamp(10px, 2vw, 16px);
  bottom: clamp(46px, 9vw, 58px);
  color: rgba(243, 246, 250, 0.94);
  font-size: clamp(12px, 2.2vw, 14px);
  min-height: clamp(32px, 6.5vw, 40px);
  min-width: clamp(62px, 13vw, 86px);
  padding: 0 clamp(12px, 2.8vw, 18px) 0 clamp(14px, 3.2vw, 20px);
  position: absolute;
  right: 0;
  z-index: 4;
}
.adsense-video-skip:disabled {
  cursor: default;
  opacity: 0.72;
}
.adsense-interstitial img {
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.34);
}
`;
  document.head.appendChild(style);
}

function resolveTarget(target: AdRenderTarget): HTMLElement {
  if (typeof target !== "string") {
    return target;
  }

  if (typeof document === "undefined") {
    throw new AdRenderError("Cannot render ad: document is not available.");
  }

  const element = document.querySelector<HTMLElement>(target);

  if (!element) {
    throw new AdRenderError(`Cannot render ad: target not found (${target}).`);
  }

  return element;
}

function emit(
  options: AdRenderOptions,
  event: AdLifecycleEvent,
  ad: AdDecision,
  error?: Error,
): void {
  try {
    options.onEvent?.(event, ad, error);
  } catch (callbackError) {
    console.error("Publisher onEvent handler threw an exception:", callbackError);
  }
}

function setImportantStyle(
  element: HTMLElement,
  property: string,
  value: string,
): void {
  // Avoid needless mutations, including priority-only changes.
  if (
    element.style.getPropertyValue(property) === value &&
    element.style.getPropertyPriority(property) === "important"
  ) return;
  element.style.setProperty(property, value, "important");
}

function setStyle(element: HTMLElement, property: string, value: string): void {
  element.style.setProperty(property, value);
}

function enforceExactBox(
  element: HTMLElement,
  width: number,
  height: number,
): void {
  const widthPx = `${width}px`;
  const heightPx = `${height}px`;

  setImportantStyle(element, "width", widthPx);
  setImportantStyle(element, "height", heightPx);
  setImportantStyle(element, "min-width", widthPx);
  setImportantStyle(element, "max-width", widthPx);
  setImportantStyle(element, "min-height", heightPx);
  setImportantStyle(element, "max-height", heightPx);
  setImportantStyle(element, "overflow", "hidden");
  setImportantStyle(element, "box-sizing", "border-box");
  setImportantStyle(element, "position", "relative");
  setImportantStyle(element, "z-index", MAX_Z_INDEX);
  setImportantStyle(element, "transform", "none");
  setImportantStyle(element, "clip-path", "none");
  setImportantStyle(element, "border-radius", "0");
  setImportantStyle(element, "opacity", "1");
  setImportantStyle(element, "filter", "none");
  setImportantStyle(element, "mask", "none");
  setImportantStyle(element, "perspective", "none");
  setImportantStyle(element, "zoom", "1");
  setImportantStyle(element, "aspect-ratio", "auto");
  setImportantStyle(element, "flex", "none");
  setImportantStyle(element, "flex-shrink", "0");
}

function enforceCreativeBox(
  element: HTMLElement,
  width: number,
  height: number,
): void {
  enforceExactBox(element, width, height);
  setImportantStyle(element, "display", "block");
  setImportantStyle(element, "border", "0");
  setImportantStyle(element, "margin", "0");
  setImportantStyle(element, "padding", "0");
}

function enforceProtectedTarget(
  element: HTMLElement,
  width: number,
  height: number,
): void {
  enforceExactBox(element, width, height);
  setImportantStyle(element, "display", "block");
  setImportantStyle(element, "overflow", "hidden");

  if (typeof element.setAttribute === "function") {
    element.setAttribute("data-adsense-slot", "true");
  }
}

function enforceProtectedDimensions(
  element: HTMLElement,
  width: number,
  height: number,
): void {
  const widthPx = `${width}px`;
  const heightPx = `${height}px`;

  for (const [property, value] of [
    ["width", widthPx],
    ["height", heightPx],
    ["min-width", widthPx],
    ["max-width", widthPx],
    ["min-height", heightPx],
    ["max-height", heightPx],
    ["margin", "0"],
    ["padding", "0"],
    ["border", "0"],
    ["border-radius", "0"],
    ["box-sizing", "border-box"],
    ["display", "block"],
    ["vertical-align", "top"],
    ["position", "relative"],
    ["z-index", MAX_Z_INDEX],
    ["overflow", "hidden"],
    ["transform", "none"],
    ["clip-path", "none"],
    ["opacity", "1"],
    ["filter", "none"],
    ["mask", "none"],
    ["perspective", "none"],
    ["zoom", "1"],
    ["aspect-ratio", "auto"],
    ["flex", "none"],
    ["flex-shrink", "0"],
  ] as const) {
    setImportantStyle(element, property, value);
  }

  if (typeof element.setAttribute === "function") {
    element.setAttribute("data-adsense-host", "true");
  }
}

function collapseAdSlot(element: HTMLElement): void {
  element.textContent = "";
  if (typeof element.removeAttribute === "function") {
    element.removeAttribute("data-adsense-slot");
  }
  element.style.setProperty("display", "none", "important");
}

function protectImageAdTampering(params: {
  targetElement: HTMLElement;
  hostElement: HTMLElement;
  iframeElement: HTMLElement;
  width: number;
  height: number;
  ad: AdDecision;
  options: AdRenderOptions;
}): () => void {
  const {
    targetElement,
    hostElement,
    iframeElement,
    width,
    height,
    ad,
    options,
  } = params;

  let locked = false;
  let disposed = false;
  let mismatchReported = false;
  const mutationObservers: MutationObserver[] = [];
  const cleanupFns: Array<() => void> = [];
  const cleanup = () => {
    disposed = true;
    for (const stop of cleanupFns) stop();
  };

  const reapplyAllStyles = () => {
    enforceProtectedTarget(targetElement, width, height);
    enforceProtectedDimensions(hostElement, width, height);
    enforceProtectedDimensions(iframeElement, width, height);

    const hasChild =
      typeof targetElement.contains === "function"
        ? targetElement.contains(hostElement)
        : Array.isArray(
            (targetElement as unknown as { children?: HTMLElement[] }).children,
          )
          ? (
              targetElement as unknown as { children: HTMLElement[] }
            ).children.includes(hostElement)
          : false;

    if (!hasChild) {
      targetElement.appendChild(hostElement);
    }

    // Mutation callbacks run after this function returns. A synchronous lock
    // alone cannot suppress our own style writes in WebKit. Drain only records
    // created during this synchronous repair; future external changes still
    // reach the observers. This also handles CSS shorthand normalization.
    for (const observer of mutationObservers) observer.takeRecords();
  };

  reapplyAllStyles();

  const handleTamperOrDrift = (source: "mutation" | "resize") => {
    if (locked || disposed) return;
    locked = true;
    try {
      const targetBox = targetElement.getBoundingClientRect();
      const hostBox = hostElement.getBoundingClientRect();
      const iframeBox = iframeElement.getBoundingClientRect();

      const hasDrift =
        (targetBox.width > 0 &&
          Math.abs(targetBox.width - width) > SIZE_TOLERANCE_PX) ||
        (targetBox.height > 0 &&
          Math.abs(targetBox.height - height) > SIZE_TOLERANCE_PX) ||
        (hostBox.width > 0 &&
          Math.abs(hostBox.width - width) > SIZE_TOLERANCE_PX) ||
        (hostBox.height > 0 &&
          Math.abs(hostBox.height - height) > SIZE_TOLERANCE_PX) ||
        (iframeBox.width > 0 &&
          Math.abs(iframeBox.width - width) > SIZE_TOLERANCE_PX) ||
        (iframeBox.height > 0 &&
          Math.abs(iframeBox.height - height) > SIZE_TOLERANCE_PX);

      if (hasDrift || source === "mutation") {
        reapplyAllStyles();
      }

      if (hasDrift && !mismatchReported) {
        mismatchReported = true;
        const driftError = new AdRenderError(
          `Ad size mismatch detected: element drifted from ${width}x${height}.`,
        );
        emit(options, "manipulation", ad, driftError);
        emit(options, "error", ad, driftError);

        if (options.strict) {
          cleanup();
          collapseAdSlot(targetElement);
        }
      }
    } finally {
      locked = false;
    }
  };

  if (typeof MutationObserver !== "undefined") {
    const targetObserver = new MutationObserver(() =>
      handleTamperOrDrift("mutation"),
    );
    mutationObservers.push(targetObserver);
    targetObserver.observe(targetElement, {
      attributes: true,
      attributeFilter: ["style", "class", "width", "height"],
      childList: true,
    });
    cleanupFns.push(() => targetObserver.disconnect());

    const hostObserver = new MutationObserver(() =>
      handleTamperOrDrift("mutation"),
    );
    mutationObservers.push(hostObserver);
    hostObserver.observe(hostElement, {
      attributes: true,
      attributeFilter: ["style", "class", "width", "height"],
      childList: true,
    });
    cleanupFns.push(() => hostObserver.disconnect());

    const iframeObserver = new MutationObserver(() =>
      handleTamperOrDrift("mutation"),
    );
    mutationObservers.push(iframeObserver);
    iframeObserver.observe(iframeElement, {
      attributes: true,
      attributeFilter: ["style", "class", "width", "height", "sandbox"],
    });
    cleanupFns.push(() => iframeObserver.disconnect());
  }

  if (typeof ResizeObserver !== "undefined") {
    const resizeObserver = new ResizeObserver(() =>
      handleTamperOrDrift("resize"),
    );
    resizeObserver.observe(targetElement);
    resizeObserver.observe(hostElement);
    resizeObserver.observe(iframeElement);
    cleanupFns.push(() => resizeObserver.disconnect());
  }

  return cleanup;
}

function createRenderToken(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function"
  ) {
    const values = new Uint32Array(4);
    crypto.getRandomValues(values);
    return Array.from(values, (value) =>
      value.toString(16).padStart(8, "0"),
    ).join("");
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function createProtectedImageDocument(
  asset: ImageAsset,
  alt: string,
  token: string,
  clickUrl?: string,
): string {
  const config = JSON.stringify({
    token,
    width: asset.width,
    height: asset.height,
  })
    .replace(/</g, "\\u003c")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
  const width = `${asset.width}px`;
  const height = `${asset.height}px`;
  const exactBox = `width:${width}!important;height:${height}!important;min-width:${width}!important;max-width:${width}!important;min-height:${height}!important;max-height:${height}!important`;
  const baseBoxStyles = `${exactBox};margin:0!important;padding:0!important;border:0!important;border-radius:0!important;box-sizing:border-box!important;display:block!important;overflow:hidden!important;position:relative!important`;

  const escapedClickUrl = clickUrl ? escapeHtmlAttribute(clickUrl) : "";
  const escapedAlt = escapeHtmlAttribute(alt);
  const escapedAssetUrl = escapeHtmlAttribute(asset.url);

  const anchorAttrs = escapedClickUrl
    ? `href="${escapedClickUrl}" target="_blank" rel="noopener sponsored" aria-label="${escapedAlt}"`
    : `role="presentation" aria-label="${escapedAlt}"`;

  const anchorStyle = `${baseBoxStyles};text-decoration:none!important;outline:none!important;cursor:${escapedClickUrl ? "pointer" : "default"}!important`;
  const imgStyle = `${baseBoxStyles};vertical-align:top!important;transform:none!important`;

  return `<!doctype html><html lang="en" style="${baseBoxStyles}"><head><meta charset="utf-8"><meta name="viewport" content="width=${asset.width},height=${asset.height},initial-scale=1"><style>html,body,a,img{${baseBoxStyles}}a{text-decoration:none!important;outline:none!important;cursor:${escapedClickUrl ? "pointer" : "default"}!important}img{vertical-align:top!important;transform:none!important}</style></head><body style="${baseBoxStyles}"><a id="creative-link" ${anchorAttrs} style="${anchorStyle}"><img id="creative" src="${escapedAssetUrl}" alt="${escapedAlt}" loading="eager" width="${asset.width}" height="${asset.height}" style="${imgStyle}"></a><script>(()=>{const c=${config};const send=(type,extra={})=>parent.postMessage({channel:"${PROTECTED_IMAGE_CHANNEL}",token:c.token,type,...extra},"*");const link=document.getElementById("creative-link");const image=document.getElementById("creative");let sent=false;if(link){link.addEventListener("click",()=>{send("click")})}const measure=()=>{const box=image.getBoundingClientRect();const documentBox=document.documentElement.getBoundingClientRect();const bodyBox=document.body.getBoundingClientRect();const linkBox=link?link.getBoundingClientRect():box;return{box,documentBox,bodyBox,linkBox}};const isReady=(m)=>m.box.width>0&&m.box.height>0&&m.documentBox.width>0&&m.documentBox.height>0&&m.bodyBox.width>0&&m.bodyBox.height>0&&m.linkBox.width>0&&m.linkBox.height>0;const checkReady=(attempts=0)=>{if(sent)return;const m=measure();if(!isReady(m)){if(attempts<60){if(typeof requestAnimationFrame==="function"){requestAnimationFrame(()=>checkReady(attempts+1))}else{setTimeout(()=>checkReady(attempts+1),16)}return}}sent=true;send("ready",{width:m.box.width,height:m.box.height,documentWidth:m.documentBox.width,documentHeight:m.documentBox.height,bodyWidth:m.bodyBox.width,bodyHeight:m.bodyBox.height,anchorWidth:m.linkBox.width,anchorHeight:m.linkBox.height})};const onLoaded=()=>{if(image.naturalWidth>0||image.width>0){checkReady()}else{sent=true;send("render-error")}};send("initialized");image.addEventListener("load",onLoaded,{once:true});image.addEventListener("error",()=>{if(!sent){sent=true;send("render-error")}},{once:true});if(image.complete){if(image.naturalWidth>0){checkReady()}else{setTimeout(()=>{if(!sent&&image.complete&&image.naturalWidth===0){sent=true;send("render-error")}},100)}}})()</script></body></html>`;
}

type ProtectedImageRender = {
  host: HTMLDivElement;
  root: ShadowRoot;
  wrapper: HTMLDivElement;
  iframe: HTMLIFrameElement;
  ready: Promise<void>;
  destroy: () => void;
};

function createProtectedImage(
  assetValue: ImageAsset,
  ad: AdDecision,
  options: AdRenderOptions,
  alt: string,
  configuration: {
    sizeHost?: boolean;
    expectedOrigin?: string;
    frameUrl?: string;
  } = {},
): ProtectedImageRender {
  if (
    !isPositiveFiniteNumber(assetValue.width) ||
    !isPositiveFiniteNumber(assetValue.height) ||
    typeof assetValue.url !== "string" ||
    !assetValue.url
  ) {
    throw new AdRenderError(
      "Cannot render image ad: valid URL and dimensions are required.",
    );
  }
  if (
    typeof window === "undefined" ||
    typeof window.addEventListener !== "function" ||
    typeof document.createElement !== "function"
  ) {
    throw new AdRenderError(
      "Cannot render image ad: protected iframe messaging is unavailable.",
    );
  }

  const asset = Object.freeze({
    url: assetValue.url,
    width: assetValue.width,
    height: assetValue.height,
  });
  const host = document.createElement("div");
  if (typeof host.attachShadow !== "function") {
    throw new AdRenderError(
      "Cannot render image ad: closed Shadow DOM is unavailable.",
    );
  }
  const root = host.attachShadow({ mode: "closed" });
  const wrapper = document.createElement("div");
  const iframe = document.createElement("iframe");
  const token = createRenderToken();

  if (configuration.sizeHost ?? true) {
    enforceProtectedDimensions(host, asset.width, asset.height);
  } else {
    setImportantStyle(host, "display", "block");
    setImportantStyle(host, "width", "100%");
    setImportantStyle(host, "position", "relative");
    setImportantStyle(host, "z-index", MAX_Z_INDEX);
  }

  enforceProtectedDimensions(wrapper, asset.width, asset.height);
  enforceProtectedDimensions(iframe, asset.width, asset.height);

  setImportantStyle(wrapper, "position", "relative");
  setImportantStyle(wrapper, "overflow", "hidden");
  setImportantStyle(wrapper, "z-index", MAX_Z_INDEX);

  setImportantStyle(iframe, "position", "relative");
  setImportantStyle(iframe, "z-index", "1");
  setImportantStyle(iframe, "overflow", "hidden");

  iframe.setAttribute("width", String(asset.width));
  iframe.setAttribute("height", String(asset.height));
  iframe.setAttribute("scrolling", "no");
  iframe.setAttribute("title", alt || "Sponsored ad creative");
  iframe.setAttribute("tabindex", "-1");
  iframe.setAttribute("sandbox", PROTECTED_IFRAME_SANDBOX);
  iframe.setAttribute("referrerpolicy", PROTECTED_IFRAME_REFERRER_POLICY);

  wrapper.appendChild(iframe);
  root.appendChild(wrapper);

  const clickUrl = resolveClickUrl(ad);

  let settled = false;
  let resolveReady!: () => void;
  let rejectReady!: (error: Error) => void;
  const ready = new Promise<void>((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });

  const cleanup = () => {
    window.removeEventListener("message", onMessage);
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  };

  const destroy = () => {
    if (!settled) {
      settled = true;
      cleanup();
      rejectReady(
        new AdRenderError("Rendering superseded by a newer ad request."),
      );
      return;
    }

    cleanup();
  };

  const fail = (message: string) => {
    if (settled) return;
    settled = true;
    cleanup();
    rejectReady(new AdRenderError(message));
  };

  const onMessage = (event: MessageEvent) => {
    if (event.source !== iframe.contentWindow) return;

    if (configuration.expectedOrigin) {
      if (event.origin !== configuration.expectedOrigin) return;
    } else {
      if (
        event.origin &&
        event.origin !== "null" &&
        typeof window !== "undefined" &&
        window.location &&
        window.location.origin &&
        event.origin === window.location.origin
      ) {
        return;
      }
    }

    const data: unknown = event.data;
    if (!data || typeof data !== "object") return;
    const message = data as Record<string, unknown>;
    if (
      message.channel !== PROTECTED_IMAGE_CHANNEL ||
      message.token !== token ||
      typeof message.type !== "string"
    ) {
      return;
    }

    if (message.type === "click") {
      if (Object.keys(message).length === 3) {
        emit(options, "click", ad);
      }
      return;
    }

    if (
      (message.type === "error" || message.type === "render-error") &&
      (Object.keys(message).length === 3 || Object.keys(message).length === 4)
    ) {
      fail("Cannot render ad: image failed to load.");
      return;
    }

    if (message.type === "ready") {
      const keyCount = Object.keys(message).length;
      if (keyCount !== 9 && keyCount !== 11) return;
      if (
        typeof message.width !== "number" ||
        typeof message.height !== "number" ||
        typeof message.documentWidth !== "number" ||
        typeof message.documentHeight !== "number" ||
        typeof message.bodyWidth !== "number" ||
        typeof message.bodyHeight !== "number"
      ) {
        return;
      }

      if (keyCount === 11) {
        if (
          typeof message.anchorWidth !== "number" ||
          typeof message.anchorHeight !== "number"
        ) {
          return;
        }
      }

      const isClose = (val: number, expected: number) =>
        Math.abs(val - expected) <= SIZE_TOLERANCE_PX;

      if (
        !isClose(message.width, asset.width) ||
        !isClose(message.height, asset.height) ||
        !isClose(message.documentWidth, asset.width) ||
        !isClose(message.documentHeight, asset.height) ||
        !isClose(message.bodyWidth, asset.width) ||
        !isClose(message.bodyHeight, asset.height) ||
        (typeof message.anchorWidth === "number" &&
          !isClose(message.anchorWidth, asset.width)) ||
        (typeof message.anchorHeight === "number" &&
          !isClose(message.anchorHeight, asset.height))
      ) {
        fail(
          `Ad rendered at ${message.width}x${message.height}, expected ${asset.width}x${asset.height}.`,
        );
        return;
      }

      if (!settled) {
        settled = true;
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        resolveReady();
      }
    }
  };

  window.addEventListener("message", onMessage);
  const timeoutId = setTimeout(
    () =>
      fail("Cannot render image ad: protected iframe initialization timed out."),
    PROTECTED_IMAGE_TIMEOUT_MS,
  );

  try {
    if (configuration.frameUrl) {
      iframe.src = configuration.frameUrl;
    } else {
      iframe.srcdoc = createProtectedImageDocument(asset, alt, token, clickUrl);
    }
  } catch {
    fail("Cannot render image ad: protected iframe setup failed.");
  }

  return { host, root, wrapper, iframe, ready, destroy };
}

function hideBoxOverflow(element: HTMLElement): void {
  setImportantStyle(element, "overflow", "hidden");
}

function useDynamicViewportHeight(element: HTMLElement, height: number): void {
  const heightValue = `min(${height}px, 100dvh)`;

  setImportantStyle(element, "height", heightValue);
  setImportantStyle(element, "min-height", heightValue);
  setImportantStyle(element, "max-height", heightValue);
}

function enforceResponsivePrerollTarget(
  element: HTMLElement,
  height: number,
): void {
  const heightValue = `min(${height}px, 100dvh)`;

  setImportantStyle(element, "width", "100%");
  setImportantStyle(element, "height", heightValue);
  setImportantStyle(element, "min-width", "0");
  setImportantStyle(element, "max-width", "100%");
  setImportantStyle(element, "min-height", heightValue);
  setImportantStyle(element, "max-height", heightValue);
  setImportantStyle(element, "overflow", "hidden");
  setImportantStyle(element, "box-sizing", "border-box");
  setImportantStyle(element, "position", "relative");
  setImportantStyle(element, "z-index", MAX_Z_INDEX);
}

function useResponsiveCreativeFill(
  element: HTMLElement,
  width: number,
  height: number,
): void {
  setImportantStyle(element, "width", "100%");
  setImportantStyle(element, "height", "100%");
  setImportantStyle(element, "min-width", "0");
  setImportantStyle(element, "max-width", "100%");
  setImportantStyle(element, "min-height", "0");
  setImportantStyle(element, "max-height", "100%");
  setImportantStyle(element, "aspect-ratio", `${width} / ${height}`);
}

function replaceOrAppend(
  target: HTMLElement,
  element: HTMLElement,
  clearExisting: boolean,
): void {
  if (clearExisting && typeof target.replaceChildren === "function") {
    target.replaceChildren(element);
    return;
  }

  if (clearExisting) {
    target.textContent = "";
  }

  target.appendChild(element);
}

function waitForLayout(): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (!settled) {
        settled = true;
        resolve();
      }
    };

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => {
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(done);
          return;
        }
        done();
      });
      setTimeout(done, 100);
      return;
    }

    setTimeout(done, 0);
  });
}

function verifyExactSize(
  element: HTMLElement,
  width: number,
  height: number,
): void {
  const box = element.getBoundingClientRect();
  const widthDiff = Math.abs(box.width - width);
  const heightDiff = Math.abs(box.height - height);

  if (widthDiff > SIZE_TOLERANCE_PX || heightDiff > SIZE_TOLERANCE_PX) {
    throw new AdRenderError(
      `Ad rendered at ${box.width}x${box.height}, expected ${width}x${height}.`,
    );
  }
}

function verifyResponsivePrerollTargetSize(
  element: HTMLElement,
  height: number,
): void {
  const box = element.getBoundingClientRect();
  const heightDiff = Math.abs(box.height - height);

  if (heightDiff > SIZE_TOLERANCE_PX || box.width <= 0) {
    throw new AdRenderError(
      `Pre-roll ad rendered at ${box.width}x${box.height}, expected a visible responsive slot with height ${height}.`,
    );
  }
}

function trackImpressionOnce(
  ad: AdDecision,
  options: AdRenderOptions,
): () => void {
  let tracked = false;

  return () => {
    if (tracked || options.trackImpression === false) {
      return;
    }

    tracked = true;

    if (ad.impressionUrl) {
      fireTrackingUrl(ad.impressionUrl);
    }

    emit(options, "impression", ad);
  };
}

function resolveClickUrl(ad: AdDecision): string | undefined {
  return ad.clickTrackingUrl || undefined;
}

async function renderBannerAd(
  targetElement: HTMLElement,
  ad: AdDecision,
  options: AdRenderOptions,
): Promise<AdDecision> {
  if (!isBannerAsset(ad.asset)) {
    throw new AdRenderError(
      "Cannot render banner ad: asset.url, asset.width, and asset.height are required.",
    );
  }

  const { width, height } = ad.asset;

  const previousCleanup = (
    targetElement as unknown as { __adsenseCleanup?: () => void }
  ).__adsenseCleanup;
  if (typeof previousCleanup === "function") {
    previousCleanup();
    delete (targetElement as unknown as { __adsenseCleanup?: () => void })
      .__adsenseCleanup;
  }

  enforceProtectedTarget(targetElement, width, height);

  const protectedImage = createProtectedImage(
    ad.asset,
    ad,
    options,
    options.alt ?? "Sponsored ad",
  );
  replaceOrAppend(
    targetElement,
    protectedImage.host,
    options.clearExisting ?? true,
  );

  const cleanupObservers = protectImageAdTampering({
    targetElement,
    hostElement: protectedImage.host,
    iframeElement: protectedImage.iframe,
    width,
    height,
    ad,
    options,
  });

  (
    targetElement as unknown as { __adsenseCleanup?: () => void }
  ).__adsenseCleanup = () => {
    cleanupObservers();
    protectedImage.destroy();
  };

  await protectedImage.ready;
  await waitForLayout();

  verifyExactSize(protectedImage.host, width, height);
  verifyExactSize(targetElement, width, height);
  verifyExactSize(protectedImage.iframe, width, height);

  emit(options, "rendered", ad);
  trackImpressionOnce(ad, options)();

  return ad;
}

function resolveVideoSize(
  targetElement: HTMLElement,
  asset: VideoAsset,
  options: AdRenderOptions,
  clampToTarget = false,
): { width: number; height: number } {
  const box = targetElement.getBoundingClientRect();

  if (
    clampToTarget &&
    isPositiveFiniteNumber(box.width) &&
    isPositiveFiniteNumber(box.height)
  ) {
    const preferredWidth = isPositiveFiniteNumber(options.width)
      ? options.width
      : isPositiveFiniteNumber(asset.width)
        ? asset.width
        : box.width;
    const preferredHeight = isPositiveFiniteNumber(options.height)
      ? options.height
      : isPositiveFiniteNumber(asset.height)
        ? asset.height
        : box.height;

    return {
      width: Math.min(preferredWidth, box.width),
      height: Math.min(preferredHeight, box.height),
    };
  }

  if (
    isPositiveFiniteNumber(options.width) &&
    isPositiveFiniteNumber(options.height)
  ) {
    return { width: options.width, height: options.height };
  }

  if (
    isPositiveFiniteNumber(asset.width) &&
    isPositiveFiniteNumber(asset.height)
  ) {
    return { width: asset.width, height: asset.height };
  }

  if (isPositiveFiniteNumber(box.width) && isPositiveFiniteNumber(box.height)) {
    return { width: box.width, height: box.height };
  }

  if (isPositiveFiniteNumber(box.width)) {
    return {
      width: box.width,
      height: Math.round((box.width * 9) / 16),
    };
  }

  return {
    width: VIDEO_FALLBACK_WIDTH,
    height: VIDEO_FALLBACK_HEIGHT,
  };
}

function createVideoElement(
  asset: VideoAsset,
  options: AdRenderOptions,
): HTMLVideoElement {
  const video = document.createElement("video");
  const videoOptions = options.video ?? {};
  const variant = videoOptions.variant ?? "simple";
  const isPreroll = variant === "preroll";

  video.className = "adsense-video-media";
  video.src = asset.url;
  video.controls = false;
  const initialMuted = videoOptions.muted ?? !isPreroll;
  setVideoMutedState(video, initialMuted);
  video.autoplay = videoOptions.autoplay ?? true;
  video.loop = videoOptions.loop ?? (isPreroll ? false : true);
  video.playsInline = videoOptions.playsInline ?? true;
  video.preload = "auto";

  const poster = videoOptions.poster || asset.posterUrl;

  if (poster) {
    video.poster = poster;
  }

  return video;
}

function setVideoMutedState(video: HTMLVideoElement, muted: boolean): void {
  video.defaultMuted = muted;

  if (muted) {
    video.setAttribute("muted", "");
  } else {
    video.removeAttribute("muted");
  }

  video.muted = muted;
}

type VideoPlayAttempt =
  | { ok: true }
  | { ok: false; error: unknown };

async function attemptVideoPlay(
  video: HTMLVideoElement,
): Promise<VideoPlayAttempt> {
  try {
    await video.play();
    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
}

function isAutoplayPolicyError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "NotAllowedError"
  );
}

function emitVideoPlayError(
  options: AdRenderOptions,
  ad: AdDecision,
  error: unknown,
): void {
  emit(
    options,
    "error",
    ad,
    error instanceof Error
      ? error
      : new AdRenderError("Cannot play video ad."),
  );
}

function resolveVideoLabels(labels?: VideoLabels): Required<VideoLabels> {
  return {
    ad: labels?.ad ?? "Ad",
    skip: labels?.skip ?? "Skip",
    skipIn: labels?.skipIn ?? "Skip in",
    endsIn: labels?.endsIn ?? "Ad ends in",
    muted: labels?.muted ?? "Muted",
    mute: labels?.mute ?? "Mute",
  };
}

function normalizeSkipAfterMs(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, value)
    : 0;
}

type VideoIconName = "play" | "pause" | "volume2" | "volumeX";

type VideoIconShape = {
  tagName: "line" | "path" | "polygon" | "rect";
  attributes: Record<string, string>;
};

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

const VIDEO_ICONS: Record<VideoIconName, VideoIconShape[]> = {
  play: [
    {
      tagName: "polygon",
      attributes: { points: "6 3 20 12 6 21 6 3" },
    },
  ],
  pause: [
    {
      tagName: "rect",
      attributes: { x: "14", y: "4", width: "4", height: "16", rx: "1" },
    },
    {
      tagName: "rect",
      attributes: { x: "6", y: "4", width: "4", height: "16", rx: "1" },
    },
  ],
  volume2: [
    {
      tagName: "path",
      attributes: { d: "M11 5 6 9H2v6h4l5 4V5Z" },
    },
    {
      tagName: "path",
      attributes: { d: "M15.54 8.46a5 5 0 0 1 0 7.07" },
    },
    {
      tagName: "path",
      attributes: { d: "M19.07 4.93a10 10 0 0 1 0 14.14" },
    },
  ],
  volumeX: [
    {
      tagName: "path",
      attributes: { d: "M11 5 6 9H2v6h4l5 4V5Z" },
    },
    {
      tagName: "line",
      attributes: { x1: "22", x2: "16", y1: "9", y2: "15" },
    },
    {
      tagName: "line",
      attributes: { x1: "16", x2: "22", y1: "9", y2: "15" },
    },
  ],
};

function createSvgElement(tagName: string): SVGElement {
  if (typeof document.createElementNS === "function") {
    return document.createElementNS(SVG_NAMESPACE, tagName);
  }

  return document.createElement(tagName) as unknown as SVGElement;
}

function createVideoIcon(name: VideoIconName): SVGElement {
  const icon = createSvgElement("svg");

  icon.setAttribute("class", "adsense-video-icon");
  icon.setAttribute("data-icon", name);
  icon.setAttribute("viewBox", "0 0 24 24");
  icon.setAttribute("fill", "none");
  icon.setAttribute("stroke", "currentColor");
  icon.setAttribute("stroke-width", "2");
  icon.setAttribute("stroke-linecap", "round");
  icon.setAttribute("stroke-linejoin", "round");
  icon.setAttribute("aria-hidden", "true");

  for (const shape of VIDEO_ICONS[name]) {
    const element = createSvgElement(shape.tagName);

    for (const [attribute, value] of Object.entries(shape.attributes)) {
      element.setAttribute(attribute, value);
    }

    icon.appendChild(element);
  }

  return icon;
}

function setVideoButtonIcon(
  button: HTMLButtonElement,
  icon: VideoIconName,
): void {
  if (typeof button.replaceChildren === "function") {
    button.replaceChildren(createVideoIcon(icon));
    return;
  }

  button.textContent = "";
  button.appendChild(createVideoIcon(icon));
}

type VideoProgressElements = {
  track: HTMLDivElement;
  fill: HTMLDivElement;
  input?: HTMLInputElement;
};

function updateVideoControls(
  video: HTMLVideoElement,
  playButton: HTMLButtonElement,
  muteButton: HTMLButtonElement,
  progress: VideoProgressElements,
  labels: Required<VideoLabels> = resolveVideoLabels(),
): void {
  playButton.dataset.state = video.paused ? "paused" : "playing";
  playButton.setAttribute("aria-label", video.paused ? "Play ad" : "Pause ad");
  setVideoButtonIcon(playButton, video.paused ? "play" : "pause");
  muteButton.dataset.state = video.muted ? "muted" : "unmuted";
  muteButton.setAttribute(
    "aria-label",
    video.muted ? labels.muted : labels.mute,
  );
  setVideoButtonIcon(muteButton, video.muted ? "volumeX" : "volume2");

  const duration = Number.isFinite(video.duration) ? video.duration : 0;
  const currentTime = Number.isFinite(video.currentTime)
    ? video.currentTime
    : 0;
  const progressValue = Math.min(currentTime, duration || currentTime);
  const progressPercentage =
    duration > 0 ? `${Math.min(100, (currentTime / duration) * 100)}%` : "0%";

  progress.fill.style.setProperty("width", progressPercentage);

  if (progress.input) {
    progress.input.max = String(duration || 0);
    progress.input.value = String(progressValue);
    return;
  }

  progress.track.setAttribute("aria-valuemax", String(duration || 0));
  progress.track.setAttribute("aria-valuenow", String(progressValue));
}

function createVideoButton(
  label: string,
  className = "adsense-video-button",
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = className;
  button.textContent = label;

  return button;
}

function openClickUrl(ad: AdDecision): void {
  const clickUrl = resolveClickUrl(ad);

  if (!clickUrl || typeof window === "undefined") {
    return;
  }

  window.open(clickUrl, "_blank", "noopener");
}

function recordClick(ad: AdDecision, options: AdRenderOptions): void {
  const clickTrackingUrl = ad.clickTrackingUrl;

  emit(options, "click", ad);

  if (clickTrackingUrl) {
    fireTrackingUrl(clickTrackingUrl);
  }
}

function cleanupTarget(targetElement: HTMLElement): void {
  if (typeof targetElement.replaceChildren === "function") {
    targetElement.replaceChildren();
    return;
  }

  targetElement.textContent = "";
}

type VideoSessionOptions = AdRenderOptions & {
  cleanupOnComplete?: boolean;
};

// Give destroyAd a way to stop media and any preroll UI timer.
function registerVideoCleanup(
  targetElement: HTMLElement,
  video: HTMLVideoElement,
  session?: VideoAdSession,
  stopTimer?: () => void,
): void {
  (targetElement as unknown as { __adsenseCleanup?: () => void })
    .__adsenseCleanup = () => {
    stopTimer?.();
    session?.destroy();
    video.pause();
    video.removeAttribute("src");
    video.load();
  };
}

function createVideoSession(
  targetElement: HTMLElement,
  ad: AdDecision,
  video: HTMLVideoElement,
  options: VideoSessionOptions,
): VideoAdSession {
  const skipAfterMs = normalizeSkipAfterMs(options.video?.skipAfterMs);
  const trackImpression = trackImpressionOnce(ad, options);
  let accumulatedPlaybackMs = 0;
  let playbackStartedAt: number | null = null;
  let completed = false;
  let destroyed = false;
  let skipAvailableEmitted = false;
  const readPlaybackMs = () =>
    playbackStartedAt === null
      ? accumulatedPlaybackMs
      : accumulatedPlaybackMs + Math.max(0, Date.now() - playbackStartedAt);
  const readSkipRemainingMs = () => Math.max(0, skipAfterMs - readPlaybackMs());
  const isSkipAvailable = () => readSkipRemainingMs() <= 0;
  const startSkipClock = () => {
    if (playbackStartedAt !== null || completed || destroyed) {
      return;
    }

    playbackStartedAt = Date.now();
  };
  const stopSkipClock = () => {
    if (playbackStartedAt === null) {
      return;
    }

    accumulatedPlaybackMs += Math.max(0, Date.now() - playbackStartedAt);
    playbackStartedAt = null;
  };
  const readDuration = () =>
    Number.isFinite(video.duration) && video.duration > 0
      ? video.duration
      : isPositiveFiniteNumber((ad.asset as VideoAsset).duration)
        ? ((ad.asset as VideoAsset).duration as number)
        : 0;

  const getState = (): VideoState => {
    const duration = readDuration();
    const currentTime = Number.isFinite(video.currentTime)
      ? video.currentTime
      : 0;
    const remainingSeconds = Math.max(
      0,
      Math.ceil((duration || currentTime) - currentTime),
    );

    return {
      currentTime,
      duration,
      remainingSeconds,
      skipAvailable: isSkipAvailable(),
      skipRemainingMs: readSkipRemainingMs(),
      muted: video.muted,
      paused: video.paused,
      completed,
    };
  };

  const notifyState = () => {
    const state = getState();

    if (state.skipAvailable && !skipAvailableEmitted) {
      skipAvailableEmitted = true;
      emit(options, "skip_available", ad);
    }

    options.video?.onVideoStateChange?.(state, session);

    return state;
  };

  const destroy = () => {
    if (destroyed) {
      return;
    }

    destroyed = true;
    stopSkipClock();
    video.pause();
    cleanupTarget(targetElement);
  };

  const play = async () => {
    const result = await attemptVideoPlay(video);

    if (!result.ok) {
      emitVideoPlayError(options, ad, result.error);
    }
  };

  const session: VideoAdSession = {
    ad,
    video,
    getState,
    play,
    pause: () => {
      video.pause();
    },
    skip: () => {
      if (completed || destroyed || !isSkipAvailable()) {
        notifyState();
        return;
      }

      completed = true;
      emit(options, "skip", ad);
      notifyState();
      destroy();
    },
    mute: () => {
      if (video.muted) {
        setVideoMutedState(video, true);
        notifyState();
        return;
      }

      setVideoMutedState(video, true);
      emit(options, "mute", ad);
      notifyState();
    },
    unmute: () => {
      if (!video.muted) {
        setVideoMutedState(video, false);
        notifyState();
        return;
      }

      setVideoMutedState(video, false);
      emit(options, "unmute", ad);
      notifyState();
    },
    toggleMute: () => {
      if (video.muted) {
        session.unmute();
        return;
      }

      session.mute();
    },
    click: () => {
      recordClick(ad, options);
    },
    destroy,
  };

  video.addEventListener("playing", () => {
    startSkipClock();
    emit(options, "play", ad);
    trackImpression();
    notifyState();
  });
  video.addEventListener("pause", () => {
    stopSkipClock();
    emit(options, "pause", ad);
    notifyState();
  });
  video.addEventListener("waiting", () => {
    stopSkipClock();
    notifyState();
  });
  video.addEventListener("stalled", () => {
    stopSkipClock();
    notifyState();
  });
  video.addEventListener("ended", () => {
    if (completed || destroyed) {
      return;
    }

    stopSkipClock();
    completed = true;
    emit(options, "complete", ad);
    notifyState();

    if (options.cleanupOnComplete) {
      destroy();
    }
  });
  video.addEventListener("timeupdate", () => {
    emit(options, "timeupdate", ad);
    notifyState();
  });
  video.addEventListener("loadedmetadata", notifyState);
  video.addEventListener("error", () => {
    emit(
      options,
      "error",
      ad,
      new AdRenderError("Cannot render video ad: media error."),
    );
  });

  notifyState();

  return session;
}

async function autoplayPrerollVideo(
  video: HTMLVideoElement,
  session: VideoAdSession,
  ad: AdDecision,
  options: AdRenderOptions,
): Promise<void> {
  const initialAttempt = await attemptVideoPlay(video);

  if (initialAttempt.ok) {
    return;
  }

  if (!video.muted && isAutoplayPolicyError(initialAttempt.error)) {
    session.mute();
    const mutedAttempt = await attemptVideoPlay(video);

    if (mutedAttempt.ok) {
      return;
    }

    emitVideoPlayError(options, ad, mutedAttempt.error);
    return;
  }

  emitVideoPlayError(options, ad, initialAttempt.error);
}

async function renderVideoAd(
  targetElement: HTMLElement,
  ad: AdDecision,
  options: AdRenderOptions,
): Promise<AdDecision> {
  if (!isVideoAsset(ad.asset)) {
    throw new AdRenderError("Cannot render video ad: asset.url is required.");
  }

  const variant = options.video?.variant ?? "simple";

  const { width, height } = resolveVideoSize(
    targetElement,
    ad.asset,
    options,
    variant === "preroll",
  );
  const video = createVideoElement(ad.asset, options);
  const shell = document.createElement("div");
  shell.className = "adsense-video-shell";
  enforceCreativeBox(shell, width, height);
  hideBoxOverflow(shell);
  enforceCreativeBox(video, width, height);
  hideBoxOverflow(video);
  shell.appendChild(video);

  if (variant === "simple") {
    enforceExactBox(targetElement, width, height);
    hideBoxOverflow(targetElement);
    replaceOrAppend(targetElement, shell, options.clearExisting ?? true);
    registerVideoCleanup(targetElement, video);

    const trackImpression = trackImpressionOnce(ad, options);

    shell.addEventListener("click", () => {
      emit(options, "click", ad);
      openClickUrl(ad);
    });
    video.addEventListener("playing", () => {
      emit(options, "play", ad);
      trackImpression();
    });
    video.addEventListener("pause", () => {
      emit(options, "pause", ad);
    });
    video.addEventListener("ended", () => {
      emit(options, "complete", ad);
    });
    video.addEventListener("error", () => {
      emit(
        options,
        "error",
        ad,
        new AdRenderError("Cannot render video ad: media error."),
      );
    });

    await waitForLayout();
    verifyExactSize(targetElement, width, height);
    verifyExactSize(shell, width, height);
    emit(options, "rendered", ad);

    if (video.autoplay) {
      try {
        await video.play();
      } catch (error) {
        emit(
          options,
          "error",
          ad,
          error instanceof Error
            ? error
            : new AdRenderError("Cannot play video ad."),
        );
      }
    }

    return ad;
  }

  const labels = resolveVideoLabels(options.video?.labels);
  const showCountdown = options.video?.showCountdown ?? true;
  const showSkipButton = options.video?.showSkipButton ?? true;
  const showMuteButton = options.video?.showMuteButton ?? true;
  const isProgressSeekable = options.video?.seekable ?? false;

  setImportantStyle(video, "z-index", "1");
  useDynamicViewportHeight(shell, height);
  useDynamicViewportHeight(video, height);
  useResponsiveCreativeFill(shell, width, height);
  useResponsiveCreativeFill(video, width, height);

  const countdown = document.createElement("div");
  countdown.className = "adsense-video-countdown";

  const controls = document.createElement("div");
  controls.className = "adsense-video-controls";
  controls.dataset.hidden = "false";

  const playButton = createVideoButton(
    "",
    "adsense-video-button adsense-video-icon-button adsense-video-play-toggle",
  );
  const muteButton = createVideoButton(
    "",
    "adsense-video-button adsense-video-icon-button adsense-video-mute-toggle",
  );
  const skipButton = createVideoButton(
    `${labels.skip}...`,
    "adsense-video-button adsense-video-skip",
  );
  const progressTrack = document.createElement("div");
  progressTrack.className = "adsense-video-progress";
  progressTrack.dataset.seekable = String(isProgressSeekable);
  const progressFill = document.createElement("div");
  progressFill.className = "adsense-video-progress-fill";
  progressFill.setAttribute("aria-hidden", "true");
  progressTrack.appendChild(progressFill);

  let progressInput: HTMLInputElement | undefined;

  if (isProgressSeekable) {
    progressInput = document.createElement("input");
    progressInput.type = "range";
    progressInput.className = "adsense-video-progress-input";
    progressInput.min = "0";
    progressInput.step = "0.1";
    progressInput.value = "0";
    progressInput.setAttribute("aria-label", "Seek ad");
    progressTrack.appendChild(progressInput);
  } else {
    progressTrack.setAttribute("role", "progressbar");
    progressTrack.setAttribute("aria-label", "Ad progress");
    progressTrack.setAttribute("aria-valuemin", "0");
    progressTrack.setAttribute("aria-valuemax", "0");
    progressTrack.setAttribute("aria-valuenow", "0");
  }

  const progress: VideoProgressElements = {
    track: progressTrack,
    fill: progressFill,
    input: progressInput,
  };
  progressTrack.addEventListener("click", (event) => {
    event.stopPropagation();
  });

  controls.appendChild(playButton);
  if (showMuteButton) {
    controls.appendChild(muteButton);
  }
  controls.appendChild(progressTrack);

  if (showCountdown) {
    shell.appendChild(countdown);
  }
  if (showSkipButton) {
    shell.appendChild(skipButton);
  }
  shell.appendChild(controls);

  enforceResponsivePrerollTarget(targetElement, height);
  replaceOrAppend(targetElement, shell, options.clearExisting ?? true);

  const session = createVideoSession(targetElement, ad, video, {
    ...options,
    cleanupOnComplete: true,
  });

  const refreshPrerollUi = () => {
    const state = session.getState();
    const remainingSkipSeconds = Math.max(
      0,
      Math.ceil(state.skipRemainingMs / 1000),
    );

    skipButton.disabled = !state.skipAvailable;
    skipButton.textContent = state.skipAvailable
      ? `${labels.skip}...`
      : `${labels.skipIn} ${remainingSkipSeconds}s`;
    countdown.textContent = `${labels.endsIn} ${state.remainingSeconds}s`;
    updateVideoControls(
      video,
      playButton,
      muteButton,
      progress,
      labels,
    );
  };
  const countdownTimer = setInterval(refreshPrerollUi, 250);
  if (
    typeof countdownTimer === "object" &&
    "unref" in countdownTimer &&
    typeof countdownTimer.unref === "function"
  ) {
    countdownTimer.unref();
  }
  const stopCountdownTimer = () => clearInterval(countdownTimer);
  registerVideoCleanup(targetElement, video, session, stopCountdownTimer);

  const playVideo = async () => {
    await session.play();
    refreshPrerollUi();
  };

  const togglePlayback = () => {
    if (video.paused) {
      void playVideo();
      return;
    }

    session.pause();
    refreshPrerollUi();
  };

  shell.addEventListener("click", (event) => {
    const target = event.target;

    if (
      target instanceof HTMLButtonElement ||
      target instanceof HTMLInputElement
    ) {
      return;
    }

    openClickUrl(ad);
    emit(options, "click", ad);
  });
  playButton.addEventListener("click", togglePlayback);
  muteButton.addEventListener("click", () => {
    session.toggleMute();
    refreshPrerollUi();
  });
  skipButton.addEventListener("click", () => {
    session.skip();
    stopCountdownTimer();
  });
  progressInput?.addEventListener("input", () => {
    video.currentTime = Number.parseFloat(progressInput.value) || 0;
    refreshPrerollUi();
  });
  video.addEventListener("playing", () => {
    refreshPrerollUi();
  });
  video.addEventListener("pause", () => {
    refreshPrerollUi();
  });
  video.addEventListener("waiting", () => {
    refreshPrerollUi();
  });
  video.addEventListener("stalled", () => {
    refreshPrerollUi();
  });
  video.addEventListener("ended", () => {
    stopCountdownTimer();
    refreshPrerollUi();
  });
  video.addEventListener("timeupdate", () => {
    refreshPrerollUi();
  });
  video.addEventListener("loadedmetadata", () => {
    refreshPrerollUi();
  });
  video.addEventListener("volumechange", () => {
    refreshPrerollUi();
  });

  await waitForLayout();
  verifyResponsivePrerollTargetSize(targetElement, height);
  refreshPrerollUi();
  emit(options, "rendered", ad);

  if (video.autoplay) {
    await autoplayPrerollVideo(video, session, ad, options);
    refreshPrerollUi();
  }

  return ad;
}

export async function createVideoAdSession(
  target: AdRenderTarget,
  ad: AdDecision,
  options: AdRenderOptions = {},
): Promise<VideoAdSession> {
  if (typeof document === "undefined") {
    throw new AdRenderError("Cannot render ad: document is not available.");
  }

  ensureSdkStyles();

  if (!isVideoAsset(ad.asset)) {
    throw new AdRenderError("Cannot render video ad: asset.url is required.");
  }

  if ((options.video?.variant ?? "simple") !== "preroll") {
    throw new AdRenderError(
      "Cannot create video ad session: variant must be preroll.",
    );
  }

  const targetElement = resolveTarget(target);
  const { width, height } = resolveVideoSize(targetElement, ad.asset, options);
  const video = createVideoElement(ad.asset, {
    ...options,
    video: { ...options.video, variant: "preroll", mode: "headless" },
  });

  enforceCreativeBox(video, width, height);
  enforceExactBox(targetElement, width, height);
  replaceOrAppend(targetElement, video, options.clearExisting ?? true);

  const session = createVideoSession(targetElement, ad, video, {
    ...options,
    cleanupOnComplete: true,
  });
  registerVideoCleanup(targetElement, video, session);

  await waitForLayout();
  verifyExactSize(targetElement, width, height);
  verifyExactSize(video, width, height);
  emit(options, "rendered", ad);

  if (video.autoplay) {
    await autoplayPrerollVideo(video, session, ad, options);
  }

  return session;
}

async function renderInterstitialAd(
  targetElement: HTMLElement,
  ad: AdDecision,
  options: AdRenderOptions,
): Promise<AdDecision> {
  if (!isBannerAsset(ad.asset)) {
    throw new AdRenderError(
      "Cannot render interstitial ad: asset.url, asset.width, and asset.height are required.",
    );
  }

  const overlay = document.createElement("div");
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.className = "adsense-interstitial";
  setImportantStyle(overlay, "position", "fixed");
  setImportantStyle(overlay, "inset", "0");
  setImportantStyle(overlay, "z-index", MAX_Z_INDEX);
  setImportantStyle(overlay, "display", "flex");
  setImportantStyle(overlay, "align-items", "center");
  setImportantStyle(overlay, "justify-content", "center");
  setImportantStyle(overlay, "background", "rgba(12, 15, 20, 0.78)");
  setImportantStyle(overlay, "box-sizing", "border-box");
  setImportantStyle(overlay, "padding", "0");

  const scrollViewport = document.createElement("div");
  setImportantStyle(scrollViewport, "position", "absolute");
  setImportantStyle(scrollViewport, "inset", "0");
  setImportantStyle(scrollViewport, "overflow", "auto");
  setImportantStyle(scrollViewport, "box-sizing", "border-box");
  setImportantStyle(scrollViewport, "padding", "24px");
  setImportantStyle(scrollViewport, "display", "flex");
  setImportantStyle(scrollViewport, "align-items", "flex-start");
  setImportantStyle(scrollViewport, "justify-content", "flex-start");

  const protectedImage = createProtectedImage(
    ad.asset,
    ad,
    options,
    options.alt ?? "Sponsored ad",
  );

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.textContent = "Close";
  closeButton.disabled = true;
  closeButton.setAttribute("aria-label", "Close ad");
  setImportantStyle(closeButton, "position", "absolute");
  setImportantStyle(closeButton, "top", "16px");
  setImportantStyle(closeButton, "right", "16px");
  setImportantStyle(closeButton, "z-index", MAX_Z_INDEX);
  setImportantStyle(closeButton, "padding", "8px 12px");
  setImportantStyle(closeButton, "border", "0");
  setImportantStyle(closeButton, "background", "#ffffff");
  setImportantStyle(closeButton, "color", "#111827");
  setImportantStyle(closeButton, "cursor", "pointer");
  closeButton.addEventListener("click", () => overlay.remove());

  scrollViewport.appendChild(protectedImage.host);
  overlay.appendChild(scrollViewport);
  overlay.appendChild(closeButton);
  replaceOrAppend(targetElement, overlay, options.clearExisting ?? true);

  const closeDelayMs = options.interstitial?.closeDelayMs ?? 0;
  setTimeout(() => {
    closeButton.disabled = false;
  }, closeDelayMs);

  await protectedImage.ready;
  await waitForLayout();

  verifyExactSize(protectedImage.host, ad.asset.width, ad.asset.height);
  verifyExactSize(protectedImage.iframe, ad.asset.width, ad.asset.height);

  emit(options, "rendered", ad);
  trackImpressionOnce(ad, options)();

  return ad;
}

async function renderNativeAd(
  targetElement: HTMLElement,
  ad: AdDecision,
  options: AdRenderOptions,
): Promise<AdDecision> {
  if (!isNativeAsset(ad.asset)) {
    throw new AdRenderError(
      "Cannot render native ad: title and description are required.",
    );
  }

  const mediaAsset = ad.asset.image ?? ad.asset.icon;
  const protectedImage = mediaAsset
    ? createProtectedImage(mediaAsset, ad, options, "", {
        sizeHost: false,
      })
    : undefined;
  const host = protectedImage?.host ?? document.createElement("div");
  let root = protectedImage?.root;

  if (!root) {
    if (typeof host.attachShadow !== "function") {
      throw new AdRenderError("Cannot render native ad: closed Shadow DOM is unavailable.");
    }
    root = host.attachShadow({ mode: "closed" });
    setImportantStyle(host, "display", "block");
    setImportantStyle(host, "width", "100%");
  }

  const card = document.createElement("div");
  card.className = "adsense-native";
  setImportantStyle(card, "display", "grid");
  setImportantStyle(
    card,
    "grid-template-columns",
    options.native?.layout === "media" ? "1fr" : "auto 1fr",
  );
  setImportantStyle(card, "gap", "12px");
  setImportantStyle(card, "align-items", "center");
  setImportantStyle(card, "box-sizing", "border-box");
  setImportantStyle(card, "width", "100%");
  setImportantStyle(card, "padding", "12px");
  setImportantStyle(card, "border", "1px solid #d7dde7");
  setImportantStyle(card, "background", "#ffffff");
  setImportantStyle(card, "color", "#111827");
  setImportantStyle(card, "font-family", "Arial, sans-serif");
  setImportantStyle(card, "position", "relative");

  const body = document.createElement("div");
  setStyle(body, "min-width", "0");

  const title = document.createElement("div");
  title.textContent = ad.asset.title;
  setStyle(title, "font-size", "15px");
  setStyle(title, "line-height", "1.3");
  setStyle(title, "font-weight", "700");

  const description = document.createElement("div");
  description.textContent = ad.asset.description;
  setStyle(description, "margin-top", "4px");
  setStyle(description, "font-size", "13px");
  setStyle(description, "line-height", "1.4");
  setStyle(description, "color", "#4b5563");

  body.appendChild(title);
  body.appendChild(description);

  if (ad.asset.cta) {
    const cta = document.createElement("div");
    cta.textContent = ad.asset.cta;
    setStyle(cta, "margin-top", "8px");
    setStyle(cta, "font-size", "12px");
    setStyle(cta, "font-weight", "700");
    setStyle(cta, "color", "#0f766e");
    body.appendChild(cta);
  }

  if (protectedImage) {
    card.appendChild(protectedImage.wrapper);
  }

  card.appendChild(body);

  const clickUrl = resolveClickUrl(ad);
  if (clickUrl) {
    const clickSurface = document.createElement("a");
    clickSurface.href = clickUrl;
    clickSurface.target = "_blank";
    clickSurface.rel = "noopener sponsored";
    clickSurface.setAttribute(
      "aria-label",
      [ad.asset.title, ad.asset.description, ad.asset.cta]
        .filter(Boolean)
        .join(". "),
    );
    setImportantStyle(clickSurface, "position", "absolute");
    setImportantStyle(clickSurface, "inset", "0");
    setImportantStyle(clickSurface, "z-index", "1");
    setImportantStyle(clickSurface, "background", "transparent");
    clickSurface.addEventListener("click", () => emit(options, "click", ad));
    card.appendChild(clickSurface);
  }

  root.appendChild(card);
  replaceOrAppend(targetElement, host, options.clearExisting ?? true);

  await waitForLayout();

  emit(options, "rendered", ad);
  trackImpressionOnce(ad, options)();

  protectedImage?.ready.catch((error) => {
    emit(
      options,
      "error",
      ad,
      error instanceof Error ? error : new AdRenderError("Cannot render native image."),
    );
  });

  return ad;
}

export async function renderAd(
  target: AdRenderTarget,
  ad: AdDecision,
  options: AdRenderOptions = {},
): Promise<AdDecision> {
  if (typeof document === "undefined") {
    throw new AdRenderError("Cannot render ad: document is not available.");
  }

  ensureSdkStyles();

  const targetElement = resolveTarget(target);

  if (ad.format === "banner") {
    return renderBannerAd(targetElement, ad, options);
  }

  if (ad.format === "video") {
    return renderVideoAd(targetElement, ad, options);
  }

  if (ad.format === "interstitial") {
    return renderInterstitialAd(targetElement, ad, options);
  }

  if (ad.format === "native") {
    return renderNativeAd(targetElement, ad, options);
  }

  throw new AdRenderError("Cannot render ad: unsupported ad format.");
}
